/**
 * Imperial ↔ metric unit conversions for measurement display.
 *
 * Used by `formatDualUnit` (and consumers like the MeasurementsSection summary
 * and the public ObjectDetails Description) to render a stored measurement
 * with its converted equivalent — e.g. `24 in (61.0 cm)` or
 * `2.5 kg (5.5 lbs)`.
 *
 * Conversion factors are exact where possible; values are rounded for display
 * to one decimal for metric and to the nearest 1/8" for imperial linear units.
 */

/** Linear: how many of the named unit fit in one meter. */
const LINEAR_PER_METER: Record<string, number> = {
  meters: 1,
  cm: 100,
  mm: 1000,
  inches: 39.3700787,
  feet: 3.28083990,
};

/** Weight: how many of the named unit fit in one kilogram. */
const WEIGHT_PER_KG: Record<string, number> = {
  kg: 1,
  g: 1000,
  lbs: 2.20462262,
  oz: 35.2739619,
};

/** Volume: how many of the named unit fit in one liter. */
const VOLUME_PER_LITER: Record<string, number> = {
  L: 1,
  mL: 1000,
  cc: 1000,
  gal: 0.264172052,
  fl_oz: 33.8140227,
};

const LINEAR_METRIC = new Set(['meters', 'cm', 'mm']);
const LINEAR_IMPERIAL = new Set(['inches', 'feet']);
const WEIGHT_METRIC = new Set(['kg', 'g']);
const WEIGHT_IMPERIAL = new Set(['lbs', 'oz']);
const VOLUME_METRIC = new Set(['L', 'mL', 'cc']);
const VOLUME_IMPERIAL = new Set(['gal', 'fl_oz']);

type System = 'metric' | 'imperial';

const systemForUnit = (unit: string): System | null => {
  if (LINEAR_METRIC.has(unit) || WEIGHT_METRIC.has(unit) || VOLUME_METRIC.has(unit)) return 'metric';
  if (LINEAR_IMPERIAL.has(unit) || WEIGHT_IMPERIAL.has(unit) || VOLUME_IMPERIAL.has(unit)) return 'imperial';
  return null;
};

const tableForUnit = (unit: string): Record<string, number> | null => {
  if (LINEAR_METRIC.has(unit) || LINEAR_IMPERIAL.has(unit)) return LINEAR_PER_METER;
  if (WEIGHT_METRIC.has(unit) || WEIGHT_IMPERIAL.has(unit)) return WEIGHT_PER_KG;
  if (VOLUME_METRIC.has(unit) || VOLUME_IMPERIAL.has(unit)) return VOLUME_PER_LITER;
  return null;
};

/**
 * Convert a numeric value between units in the same dimension category.
 * Returns NaN if either unit is unrecognized or the units are not in the
 * same category.
 */
export const convertDimension = (value: number, fromUnit: string, toUnit: string): number => {
  if (!Number.isFinite(value)) return NaN;
  if (fromUnit === toUnit) return value;
  const table = tableForUnit(fromUnit);
  if (!table || !(toUnit in table) || !(fromUnit in table)) return NaN;
  const baseValue = value / table[fromUnit];
  return baseValue * table[toUnit];
};

/** Pick the preferred conversion target for a given source unit. */
const conversionTargetFor = (fromUnit: string): string | null => {
  switch (fromUnit) {
    case 'inches':
    case 'feet':
      return 'cm';
    case 'cm':
    case 'mm':
    case 'meters':
      return 'inches';
    case 'lbs':
    case 'oz':
      return 'kg';
    case 'kg':
    case 'g':
      return 'lbs';
    case 'gal':
    case 'fl_oz':
      return 'L';
    case 'L':
    case 'mL':
    case 'cc':
      return 'gal';
    default:
      return null;
  }
};

/**
 * Parse a display value (which may include a fraction part like "1/2") into
 * a single decimal. Returns NaN if the leading numeric token can't be parsed.
 */
export const parseValueWithFraction = (value: string, fraction?: string | null): number => {
  const baseStr = (value ?? '').trim();
  if (!baseStr) return NaN;
  const base = Number(baseStr);
  if (!Number.isFinite(base)) return NaN;

  if (fraction && fraction.trim()) {
    const m = fraction.trim().match(/^(\d+)\s*\/\s*(\d+)$/);
    if (m) {
      const num = Number(m[1]);
      const den = Number(m[2]);
      if (Number.isFinite(num) && Number.isFinite(den) && den !== 0) {
        return base + num / den;
      }
    }
  }
  return base;
};

/**
 * Format a numeric value for the converted half of a dual-unit display.
 * Metric → 1 decimal place; imperial → 2 decimal places.
 */
const formatConverted = (value: number, unit: string): string => {
  const sys = systemForUnit(unit);
  const decimals = sys === 'metric' ? 1 : 2;
  return value.toFixed(decimals).replace(/\.?0+$/, '');
};

/**
 * Render `"<value> <unit> (<converted> <toUnit>)"`. Falls back to just the
 * input pair when the conversion target is unavailable.
 */
export const formatDualUnit = (
  value: number,
  unit: string,
  fraction?: string | null,
): string => {
  const display = fraction ? `${value} ${fraction}` : `${value}`;
  const target = conversionTargetFor(unit);
  if (!target) return `${display} ${unit}`;
  const numericValue = parseValueWithFraction(`${value}`, fraction ?? null);
  if (!Number.isFinite(numericValue)) return `${display} ${unit}`;
  const converted = convertDimension(numericValue, unit, target);
  if (!Number.isFinite(converted)) return `${display} ${unit}`;
  return `${display} ${unit} (${formatConverted(converted, target)} ${target})`;
};
